import {createRequestTracingHeaders} from "../utils/tracing-util";
import {removeErrors} from '../utils/error-utils'
import {removeSensitiveData, postProcess, saveSensitiveData} from "../utils/processing-utils";
import {actionContains, actionsContains} from "../utils/events/actionEvent";

declare global {
    interface Window { 
        JWT_TOKEN?: string; 
    }
}

export async function getInitUIState(flowName:string, clientCode:string, domainDataId:string|null, hashedId:string|null, channelCode:string|null, params:any) {
  // append extra queryParams to send to the back-end
  let queryParams= {
    clientCode: clientCode,
    channelCode: channelCode ? channelCode : `WEB`,
    ...params
  };

  // add id & hashedId if id exists
  if (domainDataId) {
    queryParams = {
      id:domainDataId,
      hashedId: hashedId,
      ...queryParams
    };
  }

  let queryString = Object.keys(queryParams).map(key => `${key}=${encodeURIComponent(queryParams[key])}`).join('&');
  const JWT_TOKEN = window.JWT_TOKEN;
  let Authorization = JWT_TOKEN ? `Bearer ${JWT_TOKEN}` : '';
  return fetch(`${process.env.REACT_APP_UI_SERVER_URL || ''}/ui/${flowName}/$init?${queryString}`, {
    headers: {
      'Authorization': Authorization,
      ...createRequestTracingHeaders(),
    },
  })
  .then(res => {
    return res.json()
  })
  .then(body => {
    return body.result
  })
  .catch(err => {
    console.log(err)
    return null
  }) 
}

export async function getEmptyDomainObject(objectName:string, clientCode:string) {

  return fetch(`${process.env.REACT_APP_UI_SERVER_URL || ''}/ui/domain/data/${objectName}/$empty-object?clientCode=${clientCode}&excludeChildren=false`, {
    headers: {
      ...createRequestTracingHeaders(),
    },
  })
  .then(res => {
    return res.json()
  })
  .then(body => {
    return body.result
  })
  .catch(err => {
    console.log(err)
    return null
  })    
}

export async function getNewPluginData(pluginName:string, clientCode:string, body:any) {
  return fetch(`${process.env.REACT_APP_UI_SERVER_URL || ''}/ui/pluginData/${pluginName}?clientCode=${clientCode}`, {
    method:'POST',
    headers: {
      'Content-Type': 'application/json',
      ...createRequestTracingHeaders(),
    },
    body: JSON.stringify(body)
  }).then(res => {
    return res.json()
  })
  .catch(err => {
    console.log(err)
    return null
  })
}


function formBeanToUIState(formBean:any, doNotStripSensitiveData?:boolean){
  let uiState = {} as {[key:string]:any};

  if(doNotStripSensitiveData){
    saveSensitiveData(formBean); //Saves the data in state that might not come back from the backend, to persist it in the state.
    uiState.jsonDomainData = JSON.stringify(removeErrors(formBean.DomainObject[formBean.DomainObjectName]));
  }else{
    removeSensitiveData(formBean);   //Removes sensitive data for actions that don't require to send to the backend.
    uiState.jsonDomainData = JSON.stringify(removeErrors(formBean.DomainObject[formBean.DomainObjectName]));
  }

  uiState.domainObjectName = formBean.DomainObjectName;
  uiState.clientCode = formBean.ClientCode;
  uiState.flowName = formBean.FlowName;
  uiState.channelCode = formBean.ChannelCode;
  uiState.currentViewName = formBean.CurrentViewName;
  uiState.parentViewName = formBean.ParentViewName;
  uiState.currentObjectId = formBean.CurrentObjectId;
  uiState.currentObjectName = formBean.CurrentObjectName;
  uiState.subSystem = formBean.SubSystem;
  uiState.flowStack = formBean.FlowStack;

  return uiState;
}

export async function switchFlow(formBean:any, newFlow:string, action:string, params:{}) {
  //If action is submitPayment, only then we allow sensitive data to be present in the request body. For all other action we remove it.
  let isActionSubmitPayment = actionContains(action, 'PaymentFacade.submitPayment') || actionContains(action, 'PaymentFacade.submitPaymentNoDuplicateCheck');
  let uiState = isActionSubmitPayment ? formBeanToUIState(formBean, true) : formBeanToUIState(formBean);
  let url = `${process.env.REACT_APP_UI_SERVER_URL || ''}/ui/${newFlow}/$switchFlow?`;

  let queryParams = {
    action: action,
    clientCode: uiState.clientCode,
    channelCode: uiState.channelCode,
    ...params
  } as {[key:string]:string};
  if(window.JWT_TOKEN){
      // @ts-ignore
    queryParams.JWT_TOKEN = window.JWT_TOKEN;
  }
  let queryString = Object.keys(queryParams).map(key => `${key}=${encodeURIComponent(queryParams[key])}`).join('&');
  url += queryString;
  let body = JSON.stringify(uiState);

  /*
    PostProcessing right here because we do not need to wait till the fetch call completes.
     And waiting also shows as a lag where data disappears and appears on the UI.
   */
  postProcess(formBean); // need to have null checks.

  return fetch(url, {
    method:'POST',
    headers: {
      'Content-Type': 'application/json',
      ...createRequestTracingHeaders(),
    },
    body: body
  })
    .then(res => {
      return res.json()
    })
    .catch(err => {
      console.log(err)
      return null
    })
}

export async function previousFlow(formBean:any, action = "") {
  //If action is submitPayment, only then we allow sensitive data to be present in the request body. For all other action we remove it.
  let isActionSubmitPayment = actionContains(action, 'PaymentFacade.submitPayment') || actionContains(action, 'PaymentFacade.submitPaymentNoDuplicateCheck');
  let uiState = isActionSubmitPayment ? formBeanToUIState(formBean, true) : formBeanToUIState(formBean);
  let url = `${process.env.REACT_APP_UI_SERVER_URL || ''}/ui/$previousFlow?`;

  let queryParams = {
    action: action,
    clientCode: uiState.clientCode,
    channelCode: uiState.channelCode,
  } as {[key:string]:string};

  if(window.JWT_TOKEN){
    queryParams.JWT_TOKEN = window.JWT_TOKEN;
  }
  let queryString = Object.keys(queryParams).map(key => `${key}=${encodeURIComponent(queryParams[key])}`).join('&');
  url += queryString;

  let body = JSON.stringify(uiState);

  /*
    PostProcessing right here because we do not need to wait till the fetch call completes.
     And waiting also shows as a lag where data disappears and appears on the UI.
   */
  postProcess(formBean); // need to have null checks.

  return fetch(url, {
    method:'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: body
  })
    .then(res => {
      return res.json()
    })
    .catch(err => {
      console.log(err)
      return null
    })
}

export async function submitRequest(formBean:any, actions:string[]) {

  //If action is submitPayment, only then we allow sensitive data to be present in the request body. For all other actions we remove it.
  let isActionSubmitPayment = actionsContains(actions, 'PaymentFacade.submitPayment') || actionsContains(actions, 'PaymentFacade.submitPaymentNoDuplicateCheck');
  let uiState = isActionSubmitPayment ? formBeanToUIState(formBean, true) : formBeanToUIState(formBean);

  let body = JSON.stringify(uiState);

  /*
  PostProcessing right here because we do not need to wait till the fetch call completes.
   And waiting also shows as a lag where data disappears and appears on the UI.
   */
  postProcess(formBean);
  let actionsParam = ''; 
  if(Array.isArray(actions)){
    actionsParam = encodeURIComponent((actions as string[]).join(',')); 
  }else{
    actionsParam = encodeURIComponent(actions as string);   
  }
  return fetch(`${process.env.REACT_APP_UI_SERVER_URL || ''}/ui/submitAction?action=${actionsParam}&clientCode=${uiState.clientCode}&channelCode=${uiState.channelCode}`, {
    method:'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': window.JWT_TOKEN ? `Bearer ${window.JWT_TOKEN}` : '',
      ...createRequestTracingHeaders(),
    },
    body: body
  })
  .then( res => {
    return res.json();
  })
  .catch(err => {
    console.log(err)
    return null
  })
}
